/**
 * 卡片中心重组路由映射（S5-04 / C1.3 / PAGE-MCH-CARD-014·015）
 *
 * 职责：
 * 1. 六条旧路由 → 新合并页（issue-center / transaction-center）对应 Tab 的映射表；
 * 2. 旧地址重定向目标构造（query 原样透传，tab 按映射覆盖写入）；
 * 3. 合并页 `?tab=` 持态的白名单解析（非法值回退默认 Tab）。
 *
 * 纯函数、零 React 依赖，供 App.tsx 重定向、合并页壳与单测共用。
 */

/** PAGE-MCH-CARD-014 新卡管理合并页路由 */
export const ISSUE_CENTER_PATH = '/merchant/card/issue-center';
/** PAGE-MCH-CARD-015 交易管理合并页路由 */
export const TRANSACTION_CENTER_PATH = '/merchant/card/transaction-center';

/**
 * 新卡管理 Tab（PRD 2.15 Tab 映射：单开-虚拟 / 单开-实体 / 批量开卡 / 开卡进度；
 * C1.3 冻结表增挂：实体卡预申请 / 实体卡流转，原 channel/physical-* 两叶并入本壳）
 */
export type IssueCenterTab =
  | 'SINGLE_VIRTUAL'
  | 'SINGLE_PHYSICAL'
  | 'BATCH'
  | 'PROGRESS'
  | 'PHYSICAL_APPLY'
  | 'PHYSICAL_CARDS';
/** 交易管理两个 Tab（PRD 2.16 Tab 映射：卡片交易 / 账单管理） */
export type TransactionCenterTab = 'TX' | 'STATEMENT';

const ISSUE_CENTER_TABS: readonly IssueCenterTab[] = [
  'SINGLE_VIRTUAL',
  'SINGLE_PHYSICAL',
  'BATCH',
  'PROGRESS',
  'PHYSICAL_APPLY',
  'PHYSICAL_CARDS',
];
const TRANSACTION_CENTER_TABS: readonly TransactionCenterTab[] = ['TX', 'STATEMENT'];

/** 非法 / 缺失 `?tab=` 时的回退默认值（合同 Edge-3 口径） */
export const DEFAULT_ISSUE_CENTER_TAB: IssueCenterTab = 'SINGLE_VIRTUAL';
export const DEFAULT_TRANSACTION_CENTER_TAB: TransactionCenterTab = 'TX';

/**
 * 解析新卡管理 `?tab=` 值：不在白名单（含 null/undefined/任意大小写变体）一律回退默认。
 */
export function parseIssueCenterTab(raw: string | null | undefined): IssueCenterTab {
  return ISSUE_CENTER_TABS.find((tab) => tab === raw) ?? DEFAULT_ISSUE_CENTER_TAB;
}

/**
 * 解析交易管理 `?tab=` 值：不在白名单（含 null/undefined/任意大小写变体）一律回退默认。
 */
export function parseTransactionCenterTab(raw: string | null | undefined): TransactionCenterTab {
  return TRANSACTION_CENTER_TABS.find((tab) => tab === raw) ?? DEFAULT_TRANSACTION_CENTER_TAB;
}

interface CardCenterRouteMapping {
  /** 旧路由相对 `/merchant` 段的 path（供 App.tsx 内 Route 注册，与映射键一致） */
  legacyPath: string;
  /** 新合并页绝对路径 */
  to: string;
  /** 重定向后落地的 Tab 值 */
  tab: IssueCenterTab | TransactionCenterTab;
}

/**
 * 六条旧路由映射清单（合同 S5-04 §3.3 Happy Path-4）：
 * - business/virtual-cards  → issue-center?tab=SINGLE_VIRTUAL
 * - business/physical-issue → issue-center?tab=SINGLE_PHYSICAL
 * - card/issue/batch        → issue-center?tab=BATCH
 * - card/issue/jobs         → issue-center?tab=PROGRESS
 * - card/transactions       → transaction-center?tab=TX
 * - card/statements         → transaction-center?tab=STATEMENT
 */
export const CARD_CENTER_LEGACY_ROUTES: readonly CardCenterRouteMapping[] = [
  { legacyPath: 'business/virtual-cards', to: ISSUE_CENTER_PATH, tab: 'SINGLE_VIRTUAL' },
  { legacyPath: 'business/physical-issue', to: ISSUE_CENTER_PATH, tab: 'SINGLE_PHYSICAL' },
  { legacyPath: 'card/issue/batch', to: ISSUE_CENTER_PATH, tab: 'BATCH' },
  { legacyPath: 'card/issue/jobs', to: ISSUE_CENTER_PATH, tab: 'PROGRESS' },
  { legacyPath: 'card/transactions', to: TRANSACTION_CENTER_PATH, tab: 'TX' },
  { legacyPath: 'card/statements', to: TRANSACTION_CENTER_PATH, tab: 'STATEMENT' },
];

interface CardCenterRedirectTarget {
  pathname: string;
  /** 以 `?` 开头的查询串；tab 已写入，其余旧参数原样保留 */
  search: string;
}

/**
 * 构造旧路由重定向目标（合同 Edge-1：tab 按映射取值，其余查询参数原样透传）。
 *
 * @param legacyPath 旧路由键（相对 `/merchant` 段）
 * @param search 旧地址查询串（可带 `?` 前缀，如 `?page=2`）
 * @returns `{ pathname, search }`；legacyPath 未收录时返回 null（调用方兜底）
 */
export function resolveLegacyCardCenterRedirect(
  legacyPath: string,
  search?: string | null,
): CardCenterRedirectTarget | null {
  const mapping = CARD_CENTER_LEGACY_ROUTES.find((r) => r.legacyPath === legacyPath);
  if (!mapping) {
    return null;
  }
  const params = new URLSearchParams(search ?? '');
  // 旧地址若恰好带 tab 参数，以映射值覆盖（映射优先，保证落点确定性）
  params.set('tab', mapping.tab);
  return { pathname: mapping.to, search: `?${params.toString()}` };
}
